import React from 'react';
import { Link } from 'react-router-dom';
import RequestDemoForm from '../components/RequestDemoForm';
import AnswerReceipt from '../components/AnswerReceipt';
import { getConnectorLogo } from '../components/ConnectorLogos';
import { CodePanel, Statement, StatementRow } from '../components/Statement';
import {
  ArchImage,
  ArrowLink,
  Hairline,
  Label,
  Mullions,
  Rail,
  Reveal,
  RuledSpread,
  Section,
  SectionHead,
} from '../components/System';
import Icon from '../components/Icon';
import useSeo from '../lib/useSeo';
import { SAMPLES, SAMPLE_NOTE } from '../lib/samples';

// Only list what the product actually connects to today (see v1/backend/app/connectors/factory.py).
const CONNECTORS = [
  ['POSTGRESQL', 'PostgreSQL'],
  ['MYSQL', 'MySQL'],
  ['CSV', 'CSV'],
  ['EXCEL', 'Excel'],
];

const INSIGHT_ITEMS = [
  { title: 'Ask', detail: 'Plain English. No formulas.' },
  { title: 'Sidwell calculates', detail: 'It checks your columns, then runs the maths in their database.' },
  { title: 'Open the receipt', detail: 'See the calculation, the data used, and a reference to quote.' },
];

const EGRESS_ITEMS = [
  { title: 'Check', detail: 'Every field you ask about must exist.' },
  { title: 'Run', detail: 'In their database. Uploads stay private to your firm.' },
  { title: 'Return', detail: 'A few summary rows. Never the table.' },
];

const PILLARS = [
  { title: 'Ask in plain English', detail: 'Get the answer, the chart and the calculation.', to: '/conversational-bi' },
  { title: 'Connect a database', detail: 'PostgreSQL or MySQL, in minutes.', to: '/company-data-cloud' },
  { title: 'Or a spreadsheet', detail: 'Drop in a CSV or Excel file. Ask straight away.' },
  { title: 'Private to your firm', detail: 'Your data is walled off from every other firm.', to: '/security' },
];

const SOLUTIONS = [
  { to: '/conversational-bi', image: 'boardroom', tag: 'Ask', title: 'Conversational BI', detail: 'Ask in plain English. Get the answer, the chart and the working.' },
  { to: '/company-data-cloud', image: 'facade', tag: 'Connect', title: 'Company Data Cloud', detail: 'Connect databases or spreadsheets. Nothing is copied.' },
  { to: '/pushdown-sql-engine', image: 'colonnade', tag: 'Calculate', title: 'Pushdown SQL Engine', detail: 'The maths runs in your client’s database. Exact, every time.' },
];

// The query shown in "where the data goes". One row per department, which is why
// the panel reports five rows returned.
const SQL = `SELECT department,
       SUM(actual_amount) - SUM(budget_amount) AS variance
FROM   department_spend
WHERE  period BETWEEN '2026-04-01' AND '2026-06-30'
GROUP BY department
ORDER BY variance DESC;`;

// Sample figures for the performance statement (illustrative only).
const INVOICE_BARS = [37, 55, 44, 100, 29];
const REVENUE_BARS = [40, 20, 55, 70, 35, 50, 8, 38, 60, 45, 25, 0, 50, 70, 35, 60, 25, 50, 65, 45];

function Hero() {
  return (
    <section className="relative bg-limestone">
      <Mullions />
      {/* Tall facade, bleeding off the right edge. Its left edge is set from the page centre so the
          receipt laps only about the first third of it, and most of the tower stays in view. */}
      <ArchImage
        name="tower"
        priority
        alt=""
        sizes="(min-width: 1024px) 44vw, 100vw"
        className="absolute bottom-0 right-0 top-0 hidden lg:left-[72.4%] lg:block xl:left-[calc(50%+138px)]"
        imgClassName="object-[50%_70%]"
      />
      <div className="shell z-10">
        <div className="grid-12 relative gap-y-10 pb-16 pt-12 lg:min-h-[820px] lg:items-center lg:py-16">
          <Reveal className="col-span-4 lg:col-span-5">
            <h1 className="type-h1">Backbone for Corporate Financial Enterprises</h1>
            <p className="type-lead mt-7 text-glass">
              Link a client&rsquo;s database, then ask in plain English. Sidwell calculates and provides the answer.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3">
              <a href="#request-briefing" className="btn-primary">
                Request Demo
                <Icon name="arrow" size={16} />
              </a>
              <ArrowLink to="/pushdown-sql-engine">See how the maths works</ArrowLink>
            </div>
          </Reveal>
          {/* The receipt crosses the seam between the limestone column and the photograph. */}
          <Reveal delay={120} className="col-span-4 lg:col-span-5 lg:col-start-6 xl:col-span-4">
            <AnswerReceipt variant="hero" className="relative z-10" />
          </Reveal>
        </div>
      </div>
      {/* Below the desktop layout, the facade sits under the receipt and takes the same seam. */}
      <ArchImage name="tower" sizes="100vw" className="-mt-32 h-[320px] w-full lg:hidden" imgClassName="object-[50%_70%]" />
    </section>
  );
}

function FactsLedger() {
  const cell = 'border-t border-wall/20 py-4 lg:border-l lg:border-t-0 lg:px-6 lg:py-5 lg:first:border-l-0 lg:first:pl-0';
  return (
    <section aria-label="Product facts" className="relative border-y border-wall/25 bg-white">
      <div className="shell">
        <dl className="m-0 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr_1.2fr_1fr] [&>div:first-child]:border-t-0">
          <div className={cell}>
            <dt className="label mb-1 text-glass">Works with</dt>
            <dd className="m-0 flex flex-wrap gap-x-4 font-semibold">
              {CONNECTORS.map(([key, name]) => (
                <span key={key}>{getConnectorLogo(key, { size: 20 }) || name}</span>
              ))}
            </dd>
          </div>
          <div className={cell}>
            <dt className="label mb-1 text-glass">Rows copied</dt>
            <dd className="m-0 font-semibold figure">0</dd>
          </div>
          <div className={cell}>
            <dt className="label mb-1 text-glass">With every number</dt>
            <dd className="m-0 font-semibold">A receipt</dd>
          </div>
          <div className={cell}>
            <dt className="label mb-1 text-glass">Your data</dt>
            <dd className="m-0 font-semibold">Private to your firm</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

const sample = SAMPLES[0];

function StepVisual({ step }) {
  if (step === 0) {
    return (
      <Statement title="Your question" shadow={false}>
        <p className="border-t border-wall/70 px-5 py-8 font-display text-[clamp(24px,2.3vw,34px)] leading-[1.22] sm:px-7">
          {sample.q}
        </p>
      </Statement>
    );
  }
  if (step === 1) {
    return (
      <div className="bg-white">
        <CodePanel title="Run in their database" code={sample.sql} language="sql" meta={sample.time} />
      </div>
    );
  }
  return (
    <Statement title="The receipt" meta={SAMPLE_NOTE} shadow={false}>
      <div className="border-t border-wall/70">
        <StatementRow label="The data used" value={sample.data} valueClass="font-mono text-[13px]" />
        <StatementRow label="Rows sent back" value={sample.rowsBack} />
        <StatementRow label="Rows copied" value="0" />
        <StatementRow label="Reference to quote" value={sample.reference} valueClass="font-mono text-[13px]" />
      </div>
    </Statement>
  );
}

// A stepped sequence. Desktop: each step beside its own visual. Mobile: a simple stack.
function Grind() {
  return (
    <Section id="pipeline" tone="limestone" pad="none" className="pt-[clamp(72px,10vw,152px)]">
      <div className="shell">
        <SectionHead
          layout="offset"
          title="Sidwell moves the grind to the machine."
          aside="Finance teams face endless manual data tasks. Sidwell takes them on, so your team can focus on the answers."
        />
        <ol className="m-0 mt-12 list-none border-b border-wall/20 p-0 lg:mt-20">
          {INSIGHT_ITEMS.map((s, i) => (
            <li key={s.title} className="relative py-8 lg:py-12">
              <Hairline tone={i === 0 ? 'bronze' : 'wall'} className="absolute inset-x-0 top-0 opacity-40" />
              <Reveal className="grid-12 items-center gap-y-6">
                <div className="col-span-4 lg:col-span-4">
                  <h3 className="type-h3 !text-[clamp(28px,2.6vw,40px)] !font-light">{s.title}</h3>
                  <p className="mt-3 max-w-[36ch] text-glass">{s.detail}</p>
                </div>
                <div className="col-span-4 min-w-0 lg:col-span-7 lg:col-start-6">
                  <StepVisual step={i} />
                </div>
              </Reveal>
            </li>
          ))}
        </ol>
      </div>
      {/* The photograph crosses the seam into the platform index. */}
      <div className="shell mt-16 lg:mt-20">
        <ArchImage
          name="atrium"
          sizes="(min-width: 1360px) 1280px, 100vw"
          className="relative z-10 -mb-16 aspect-[16/9] w-full lg:-mb-24 lg:aspect-[21/9]"
        />
      </div>
    </Section>
  );
}

function PlatformIndex() {
  return (
    <Section id="company-data-cloud" tone="white" pad="none" className="pb-[clamp(72px,10vw,152px)] pt-[calc(clamp(72px,10vw,152px)+64px)] lg:pt-[calc(clamp(72px,10vw,152px)+96px)]">
      <div className="shell">
        <SectionHead label="The platform" title="Three parts. One exact answer." aside="Ask, connect and calculate, all in one place." />
        <ol className="m-0 mt-12 list-none border-b border-wall/25 p-0 lg:mt-16">
          {SOLUTIONS.map((s) => (
            <li key={s.title} className="border-t border-wall/25">
              <Link to={s.to} className="group relative grid-12 gap-y-3 py-7 transition-colors lg:min-h-[176px] lg:items-center lg:py-9">
                <span className="col-span-4 lg:col-span-1">
                  <span className="label text-bronze-deep">{s.tag}</span>
                </span>
                <h3 className="col-span-4 font-display text-[clamp(32px,3.6vw,56px)] font-light leading-[1.06] tracking-[-0.012em] transition-colors group-hover:text-bronze-deep lg:col-span-5">
                  {s.title}
                </h3>
                <p className="col-span-4 max-w-[38ch] text-glass lg:col-span-3">{s.detail}</p>
                {/* Always shown, on every input type. Hover and keyboard focus enlarge it. */}
                <span className="col-span-4 lg:col-span-3 lg:justify-self-end">
                  <span className="relative block aspect-[16/8] w-full overflow-hidden lg:aspect-[2/1] lg:w-[min(100%,280px)]">
                    <ArchImage name={s.image} settle={false} sizes="(min-width: 1024px) 280px, 100vw" className="h-full w-full" imgClassName="transition-transform duration-700 group-hover:scale-[1.06] group-focus-visible:scale-[1.06]" />
                    <span className="absolute bottom-0 right-0 hidden h-10 w-10 items-center justify-center bg-white transition-colors group-hover:bg-wall group-hover:text-limestone group-focus-visible:bg-wall group-focus-visible:text-limestone lg:flex">
                      <Icon name="arrow" size={18} />
                      <span className="sr-only">Learn more</span>
                    </span>
                  </span>
                </span>
                <span className="col-span-4 mt-1 inline-flex items-center gap-2 font-semibold lg:hidden">
                  Learn more <Icon name="arrow" size={16} />
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </Section>
  );
}

// A building section: the client's database stays inside; only totals cross the line.
function BuildingSection() {
  const floors = [0, 1, 2, 3];
  return (
    <figure className="m-0 border border-limestone/20 bg-spandrel/60" aria-label="Your client's database stays inside. Only totals cross the line.">
      <div className="grid grid-cols-1 md:grid-cols-[1.6fr_auto_1fr]">
        <div className="p-5 sm:p-7">
          <Label dark className="mb-4">Inside your client&rsquo;s walls</Label>
          <p className="font-display text-[clamp(22px,2vw,28px)] font-light leading-tight">Your client&rsquo;s database</p>
          <div aria-hidden="true" className="mt-6">
            {floors.map((f) => (
              <div key={f} className="grid grid-cols-6 gap-[3px] border-t border-limestone/25 py-[6px]">
                {Array.from({ length: 12 }, (_, i) => (
                  <span key={i} className="h-[11px] bg-glass/50" />
                ))}
              </div>
            ))}
            <div className="border-t-2 border-limestone/60" />
          </div>
          <p className="mt-3 text-[14px] text-glass-light">The maths runs in their database.</p>
        </div>

        <div className="relative flex items-center justify-center md:w-[56px]" aria-hidden="true">
          <span className="block h-px w-full bg-bronze md:h-full md:w-px" />
        </div>

        <div className="flex flex-col justify-center p-5 sm:p-7">
          <Label dark className="mb-4">Crosses the line</Label>
          <p className="font-display text-[clamp(22px,2vw,28px)] font-light leading-tight">Only totals</p>
          <dl className="m-0 mt-5 border-b border-limestone/20 text-[15px]">
            <div className="flex justify-between gap-4 border-t border-limestone/20 py-2.5">
              <dt className="text-glass-light">Rows returned</dt>
              <dd className="m-0 font-semibold figure">5</dd>
            </div>
            <div className="flex justify-between gap-4 border-t border-limestone/20 py-2.5">
              <dt className="text-glass-light">Rows copied</dt>
              <dd className="m-0 font-semibold figure">0</dd>
            </div>
          </dl>
        </div>
      </div>
    </figure>
  );
}

function WhereTheDataGoes() {
  return (
    <Section id="where-your-data-goes" tone="wall">
      <div className="shell">
        <SectionHead
          dark
          layout="statement"
          label="Where the data goes"
          title="Nowhere. It stays put."
          aside="The maths runs in their database. Only totals come back."
        />
        <div className="grid-12 mt-12 gap-y-8 lg:mt-16">
          <Reveal className="col-span-4 lg:col-span-6">
            <BuildingSection />
          </Reveal>
          <Reveal delay={100} className="col-span-4 min-w-0 lg:col-span-6">
            <CodePanel
              className="h-full border border-limestone/20"
              title="Sent to their database"
              meta="6 lines"
              code={SQL}
              language="sql"
              footer={
                <>
                  <span>5 rows returned</span>
                  <span>0 rows copied</span>
                </>
              }
            />
          </Reveal>
        </div>
        <ol className="m-0 mt-12 grid list-none grid-cols-1 gap-x-[clamp(16px,2vw,28px)] gap-y-6 p-0 md:grid-cols-3 lg:mt-16">
          {EGRESS_ITEMS.map((item) => (
            <Reveal as="li" key={item.title} className="border-t border-limestone/25 pt-4">
              <h3 className="type-h3 mb-2">{item.title}</h3>
              <p className="text-glass-light">{item.detail}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </Section>
  );
}

// Every chart starts at zero. Bars are one hue; a single direct label carries the headline.
function Bars({ values, height = 120, title, describe, gap = 'gap-[3px]', labelMax }) {
  const max = Math.max(...values);
  return (
    <figure className="m-0">
      <div role="img" aria-label={title} className={`flex items-end border-b border-wall ${gap}`} style={{ height }}>
        {values.map((v, i) => (
          <div key={i} title={describe(v, i)} className="group/bar relative flex h-full flex-1 items-end">
            <div
              className={`w-full bg-glass transition-colors group-hover/bar:bg-wall ${v === max && labelMax ? 'bg-wall' : ''}`}
              style={{ height: `${(v / max) * 100}%` }}
            />
            {v === max && labelMax && (
              <span className="absolute left-0 figure text-[14px] font-semibold" style={{ bottom: `calc(${(v / max) * 100}% + 2px)` }}>
                {labelMax}
              </span>
            )}
          </div>
        ))}
      </div>
    </figure>
  );
}

function PerformanceStatement() {
  return (
    <section className="relative bg-limestone" aria-labelledby="performance-title">
      <div className="relative h-[280px] sm:h-[420px] lg:h-[560px]">
        <ArchImage name="skyline" sizes="100vw" className="h-full w-full" />
      </div>
      <Mullions />
      <div className="shell relative -mt-[120px] pb-[clamp(72px,10vw,152px)] sm:-mt-[200px] lg:-mt-[300px]">
        <Reveal className="statement shadow-raised">
          <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2 px-5 pb-5 pt-6 sm:px-8">
            <h2 id="performance-title" className="type-h3 !text-[clamp(26px,2.4vw,36px)] !font-light">Sample performance statement</h2>
            <p className="border border-wall px-3 py-1.5 text-[14px] font-semibold">{SAMPLE_NOTE}</p>
          </div>
          <div className="grid grid-cols-1 border-t border-wall/70 lg:grid-cols-3">
            <article className="px-5 py-6 sm:px-8 lg:border-r lg:border-stone">
              <h3 className="font-sans text-[17px] font-semibold">Outstanding Invoices</h3>
              <dl className="m-0 mt-3 text-[15px]">
                <div className="statement-row">
                  <dt className="text-glass">On track</dt>
                  <dd className="m-0 font-medium figure">$86,500</dd>
                </div>
                <div className="statement-row border-b border-stone">
                  <dt className="text-glass">Overdue (&gt;30d)</dt>
                  <dd className="m-0 font-medium text-oxide figure">$12,400</dd>
                </div>
              </dl>
              <div className="mt-11">
                <Bars
                  values={INVOICE_BARS}
                  labelMax="$42k"
                  gap="gap-2"
                  height={150}
                  title="Five sample invoice balances. The largest is $42k."
                  describe={(v) => `${v}% of the largest balance`}
                />
              </div>
              <p className="mt-2 text-[13px] text-glass">Largest balance labelled. Bars start at zero.</p>
            </article>

            <article className="border-t border-stone px-5 py-6 sm:px-8 lg:border-r lg:border-t-0">
              <h3 className="font-sans text-[17px] font-semibold">Tax Reserve Status</h3>
              <dl className="m-0 mt-3 text-[15px]">
                <div className="statement-row">
                  <dt className="text-glass">Burn deceleration</dt>
                  <dd className="m-0 font-medium figure">$84,200</dd>
                </div>
                <div className="statement-row border-b border-stone">
                  <dt className="text-glass">Target runway</dt>
                  <dd className="m-0 font-medium figure">88% funded</dd>
                </div>
              </dl>
              <div className="mt-11">
                <div className="mb-1 flex items-baseline justify-between text-[14px]">
                  <span className="font-semibold figure">88%</span>
                  <span className="text-glass figure">100%</span>
                </div>
                <div
                  role="img"
                  aria-label="Tax reserve is 88 percent funded"
                  title="88% funded"
                  className="relative h-[18px] border-b border-wall bg-glass-tint"
                >
                  <div className="h-full bg-glass" style={{ width: '88%' }} />
                </div>
              </div>
              <p className="mt-2 text-[13px] text-glass">Funded share of the target. Bar starts at zero.</p>
            </article>

            <article className="border-t border-stone px-5 py-6 sm:px-8 lg:border-t-0">
              <h3 className="font-sans text-[17px] font-semibold">Revenue Growth</h3>
              <div className="statement-row mt-3 border-b border-stone text-[15px]">
                <span className="font-display text-[clamp(26px,2.3vw,34px)] font-light leading-none figure">$1,284,000</span>
                <span className="whitespace-nowrap font-medium text-patina figure">+12.4% QoQ</span>
              </div>
              <div className="mt-11">
                <Bars
                  values={REVENUE_BARS}
                  height={150}
                  gap="gap-[2px]"
                  title="Sample revenue index from January to September."
                  describe={(v) => `Sample index ${v}`}
                />
              </div>
              <div className="mt-2 flex justify-between text-[13px] text-glass">
                <span>Jan</span>
                <span>Mar</span>
                <span>May</span>
                <span>Jul</span>
                <span>Sep</span>
              </div>
            </article>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Capabilities() {
  return (
    <Section id="capabilities" tone="stone">
      <div className="shell">
        <Rail label="Core features" title="What you get." aside="Four things, working together.">
          <RuledSpread items={PILLARS} cols={2} notes />
        </Rail>
      </div>
    </Section>
  );
}

function RequestDemo() {
  return (
    <section id="request-briefing" className="on-dark relative z-10 bg-wall text-limestone">
      <ArchImage name="atrium" sizes="100vw" className="absolute inset-0 h-full w-full !bg-wall" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-wall/90 via-wall/70 to-wall/30" />
      <Mullions dark />
      <div className="shell relative">
        <div className="grid-12 gap-y-10 pb-0 pt-[clamp(72px,10vw,152px)]">
          <Reveal className="col-span-4 lg:col-span-5">
            <Label dark className="mb-5">Request a demo</Label>
            <h2 className="type-h1-compact">Bring us a hard question.</h2>
            <p className="type-lead mt-5 text-limestone">We&rsquo;ll answer it live, on your numbers.</p>
          </Reveal>
          {/* The white panel crosses the section boundary into the footer. */}
          <div className="relative z-10 col-span-4 -mb-16 lg:col-span-6 lg:col-start-7 lg:-mb-[120px]">
            <RequestDemoForm />
          </div>
        </div>
      </div>
    </section>
  );
}

function Home() {
  useSeo({ path: '/' });

  return (
    <main className="relative w-full">
      <Hero />
      <FactsLedger />
      <Grind />
      <PlatformIndex />
      <WhereTheDataGoes />
      <PerformanceStatement />
      <Capabilities />
      <Hairline tone="bronze" />
      <RequestDemo />
    </main>
  );
}

export default Home;
